import { http, unwrapData } from '@noor/api';
import { env } from '@noor/config';
import * as z from 'zod';
import {
  rbacRouteSchema,
  subjectSchema,
  grantSchema,
  matrixSchema,
  auditPageSchema,
} from '../model/schema';

const options = { baseURL: env.VITE_CORE_API_URL };
const base = '/api/v1/authz';
const metaSchema = z.object({
  total: z.number().int().nonnegative().optional(),
  page_count: z.number().int().nonnegative().optional(),
});
export interface CatalogParams {
  page: number;
  pageSize: number;
  q?: string | undefined;
  vertical?: string | undefined;
  include_deprecated?: boolean | undefined;
}
const pagination = ({ page, pageSize }: CatalogParams) => ({
  'pagination.limit': pageSize,
  'pagination.offset': (page - 1) * pageSize,
});
function page<T extends z.ZodType>(
  raw: unknown,
  schema: T,
  key: string,
  params: CatalogParams,
) {
  const envelope = z
    .object({ list: z.unknown().optional(), meta: metaSchema.optional() })
    .passthrough()
    .parse(raw);
  const data = unwrapData(raw) as Record<string, unknown>;
  const list = z.array(schema).parse(envelope.list ?? data[key]);
  const total = envelope.meta?.total ?? list.length;
  // Bare responses are supported for local fixtures; gateway lists are paginated.
  return {
    list:
      envelope.meta?.total === undefined
        ? list.slice((params.page - 1) * params.pageSize, params.page * params.pageSize)
        : list,
    total,
    pageCount: envelope.meta?.page_count ?? Math.ceil(total / params.pageSize),
  };
}
export const rbacCatalogApi = {
  async routes(params: CatalogParams) {
    const raw = await http.get<unknown>(`${base}/routes`, {
      ...options,
      query: {
        ...pagination(params),
        search: params.q,
        vertical: params.vertical,
        include_deprecated: params.include_deprecated,
      },
    });
    return page(raw, rbacRouteSchema, 'routes', params);
  },
  async subjects(params: CatalogParams) {
    return page(
      await http.get<unknown>(`${base}/subjects`, {
        ...options,
        query: pagination(params),
      }),
      subjectSchema,
      'subjects',
      params,
    );
  },
  async matrix(params: CatalogParams) {
    const raw = await http.get<unknown>(`${base}/matrix`, {
      ...options,
      query: { ...pagination(params), search: params.q, vertical: params.vertical },
    });
    const parsed = matrixSchema.parse(unwrapData(raw));
    const envelope = z.object({ meta: metaSchema.optional() }).parse(raw);
    return {
      ...parsed,
      routes:
        envelope.meta?.total === undefined
          ? parsed.routes.slice(
              (params.page - 1) * params.pageSize,
              params.page * params.pageSize,
            )
          : parsed.routes,
      total: envelope.meta?.total ?? parsed.routes.length,
      pageCount:
        envelope.meta?.page_count ?? Math.ceil(parsed.routes.length / params.pageSize),
    };
  },
  async grants(
    params: CatalogParams & {
      route_id?: string | undefined;
      subject_id?: number | undefined;
      user_id?: string | undefined;
    },
  ) {
    const { route_id, subject_id, user_id } = params;
    return page(
      await http.get<unknown>(`${base}/grants`, {
        ...options,
        query: { ...pagination(params), route_slug: route_id, subject_id, user_id },
      }),
      grantSchema,
      'grants',
      params,
    );
  },
  async audit(params: CatalogParams) {
    const schema = z.object({ entries: auditPageSchema.shape.entries });
    const raw = await http.get<unknown>(`${base}/audit`, {
      ...options,
      query: pagination(params),
    });
    const envelope = z
      .object({ list: z.unknown().optional(), meta: metaSchema.optional() })
      .passthrough()
      .parse(raw);
    const data = unwrapData(raw) as Record<string, unknown>;
    const list = schema.parse({ entries: envelope.list ?? data.entries }).entries;
    const total = envelope.meta?.total ?? list.length;
    return {
      list:
        envelope.meta?.total === undefined
          ? list.slice((params.page - 1) * params.pageSize, params.page * params.pageSize)
          : list,
      total,
      pageCount: envelope.meta?.page_count ?? Math.ceil(total / params.pageSize),
    };
  },
  async renameSubject(id: number, title: string) {
    return subjectSchema.parse(
      unwrapData(
        await http.patch<unknown>(
          `${base}/subjects/${id}`,
          { title: title.trim() },
          options,
        ),
      ),
    );
  },
};

/** Read every page for an access editor: an unseen grant must never be mistaken for a missing grant. */
export async function collectCatalog<T>(
  fetch: (params: CatalogParams) => Promise<{ list: T[]; pageCount: number }>,
): Promise<T[]> {
  const first = await fetch({ page: 1, pageSize: 100 });
  const result = [...first.list];
  for (let page = 2; page <= first.pageCount; page++)
    result.push(...(await fetch({ page, pageSize: 100 })).list);
  return result;
}
